import { create } from "zustand";
import { persist } from "zustand/middleware";
import { indexedDBStorage } from "../utils/indexedDBStorage";
import { listContacts } from "./apiStore";

export const useContactStore = create(
  persist(
    (set, get) => ({
      contacts: [],
      contactsTotal: 0,
      loading: false,
      lastFetched: 0,

      setContacts: (contacts, total) => 
        set(() => ({ 
          contacts: Array.isArray(contacts) ? contacts : [],
          contactsTotal: total || contacts?.length || 0,
        })),

      clearContacts: () => 
        set(() => ({ 
          contacts: [],
          contactsTotal: 0,
          lastFetched: 0,
        })),

      // Synchronize 15,000+ contacts natively, fetching everything from the backend
      fetchAndSyncContacts: async (force = false) => {
        const { lastFetched } = get();
        const now = Date.now();
        
        // Cache valid for 3 minutes unless forced
        if (!force && now - lastFetched < 3 * 60 * 1000) {
           return; 
        }

        set({ loading: true });
        try {
          // Fetch up to 15,000 to cover large accounts all at once
          const res = await listContacts({ page: 1, limit: 15000 });
          const newContacts = res?.contacts || res?.data || [];
          set({ 
             contacts: newContacts, 
             contactsTotal: res?.total || newContacts.length,
             lastFetched: now,
             loading: false 
          });
        } catch (error) {
          console.error("Failed to sync backend contacts into IndexedDB:", error);
          set({ loading: false });
        }
      },

      updateSingleContactLocally: (id, updates) => {
        set((state) => ({
           contacts: state.contacts.map((c) => (c.id === id ? { ...c, ...updates } : c))
        }));
      },

      addContactLocally: (newContact) => {
        set((state) => {
          const filtered = state.contacts.filter(c => c.phone !== newContact.phone && c.id !== newContact.id);
          return {
            contacts: [newContact, ...filtered],
            contactsTotal: state.contactsTotal + (state.contacts.length === filtered.length ? 1 : 0)
          };
        });
      },
      
      removeContactsLocally: (idsArray) => {
        const idsSet = new Set(idsArray);
        set((state) => ({
           contacts: state.contacts.filter((c) => !idsSet.has(c.id)),
           contactsTotal: Math.max(0, state.contactsTotal - idsSet.size)
        }));
      },

      updateMultipleContactsLocally: (idsArray, updates) => {
         const idsSet = new Set(idsArray);
         set((state) => ({
            contacts: state.contacts.map((c) => 
               idsSet.has(c.id) ? { ...c, ...updates } : c
            )
         }));
      }
    }),
    {
      name: "contacts",
      storage: indexedDBStorage,
    }
  )
);
